/**
 * Prioritization columns ($Qualified, $Reason, $Score, $Tier): **Leaderboard** icon (same as sidebar
 * “Prioritize Prospects”) at the trailing edge, matching {@link PrimaryColumnHeaderIconExtension}.
 * These columns are never AI-enabled; no overlap handling with the AI icon is needed.
 * Z-index 88: below primary key icon (90), below AI (100) on other columns.
 */
import type { IScale } from '@univerjs/core';
import { SheetExtension, SHEET_EXTENSION_TYPE } from '@univerjs/engine-render';
import type { SpreadsheetSkeleton } from '@univerjs/engine-render';
import type { UniverRenderingContext } from '@univerjs/engine-render';

const PRIORITIZATION_ICON_SIZE = 13;
const ICON_PADDING_RIGHT = 4;
const GAP_AFTER_TEXT = 12;
const HEADER_TEXT_PADDING_LEFT = 6;
const HEADER_FONT = '12px "Helvetica Neue", Helvetica, Arial, sans-serif';

const DEFAULT_ICON_COLOR = '#49BAFD';

/** Exact `path` from `@mui/icons-material` **Leaderboard** (WebNavSidebar Prioritize Prospects). */
function getLeaderboardIconSvgDataUrl(fillColor: string): string {
  const fill = fillColor.replace(/"/g, '&quot;');
  const d = 'M7.5 21H2V9h5.5zm7.25-18h-5.5v18h5.5zM22 11h-5.5v10H22z';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${fill}"><path d="${d}"/></svg>`;
  return 'data:image/svg+xml,' + encodeURIComponent(svg);
}

const imageCacheByColor: Record<string, HTMLImageElement> = {};
let loadCallback: (() => void) | null = null;

function getIconImage(
  iconColor: string,
  onLoaded?: () => void
): HTMLImageElement {
  const key = iconColor || DEFAULT_ICON_COLOR;
  const cached = imageCacheByColor[key];
  if (cached?.complete && cached.naturalWidth > 0) {
    return cached;
  }
  const img = new Image();
  loadCallback = onLoaded ?? null;
  img.onload = () => {
    loadCallback?.();
    loadCallback = null;
  };
  img.src = getLeaderboardIconSvgDataUrl(key);
  imageCacheByColor[key] = img;
  return img;
}

export type GetPrioritizationColumnIndices = () => Set<number>;
export type GetColumnDisplayName = (colIndex: number) => string;

export function createPrioritizationColumnHeaderIconExtension(
  getPrioritizationColumnIndices: GetPrioritizationColumnIndices,
  onIconLoaded?: () => void,
  iconColor: string = DEFAULT_ICON_COLOR,
  getColumnDisplayName?: GetColumnDisplayName
): SheetExtension {
  const fillColor = iconColor || DEFAULT_ICON_COLOR;
  getIconImage(fillColor, onIconLoaded);

  class PrioritizationColumnHeaderIconExtension extends SheetExtension {
    override uKey = 'PrioritizationColumnHeaderIconExtension';
    override type = SHEET_EXTENSION_TYPE.GRID;
    protected override Z_INDEX = 88;

    override draw(
      ctx: UniverRenderingContext,
      parentScale: IScale,
      skeleton: SpreadsheetSkeleton
    ): void {
      const tx = this.translateX ?? 0;
      const ty = this.translateY ?? 0;

      const prioritizationCols = getPrioritizationColumnIndices();
      if (prioritizationCols.size === 0) return;

      const sk = skeleton as unknown as {
        getColumnCount?: () => number;
        columnHeaderHeight?: number;
        columnWidthAccumulation?: number[];
        rowColumnSegment?: { startColumn: number; endColumn: number };
      };
      const columnCount =
        sk.getColumnCount?.() ?? sk.columnWidthAccumulation?.length ?? 0;
      if (columnCount <= 0) return;

      const scaleX = parentScale?.scaleX ?? 1;
      const scaleY = parentScale?.scaleY ?? 1;
      const columnHeaderHeight = sk.columnHeaderHeight ?? 24;
      const headerH = columnHeaderHeight * scaleY;
      const iconY = ty + (headerH - PRIORITIZATION_ICON_SIZE) / 2;

      const columnWidthAccumulation = sk.columnWidthAccumulation;
      if (
        !Array.isArray(columnWidthAccumulation) ||
        columnWidthAccumulation.length === 0
      )
        return;

      const segment = sk.rowColumnSegment;
      const startCol =
        segment && segment.startColumn >= 0 && segment.endColumn >= 0
          ? segment.startColumn
          : 0;
      const endCol =
        segment && segment.startColumn >= 0 && segment.endColumn >= 0
          ? Math.min(segment.endColumn + 1, columnCount)
          : columnCount;

      const img = getIconImage(fillColor);

      ctx.save();
      ctx.font = HEADER_FONT;

      for (let col = startCol; col < endCol; col++) {
        if (!prioritizationCols.has(col)) continue;

        const startX = col === 0 ? 0 : (columnWidthAccumulation[col - 1] ?? 0);
        const endX = columnWidthAccumulation[col] ?? startX;
        const cellW = (endX - startX) * scaleX;

        const cellLeft = tx + startX * scaleX;
        const rightEdge = tx + endX * scaleX;

        const minCellW = PRIORITIZATION_ICON_SIZE + ICON_PADDING_RIGHT;
        if (cellW < minCellW) continue;

        const preferredX =
          rightEdge - PRIORITIZATION_ICON_SIZE - ICON_PADDING_RIGHT;
        const maxX = preferredX;

        let x = preferredX;
        if (getColumnDisplayName) {
          const label = getColumnDisplayName(col) ?? '';
          const textWidth = ctx.measureText(label).width;
          const minX =
            cellLeft + HEADER_TEXT_PADDING_LEFT + textWidth + GAP_AFTER_TEXT;
          x = Math.max(minX, preferredX);
        }
        x = Math.min(x, maxX);
        if (x < cellLeft) continue;

        if (img.complete && img.naturalWidth > 0) {
          ctx.drawImage(
            img,
            x,
            iconY,
            PRIORITIZATION_ICON_SIZE,
            PRIORITIZATION_ICON_SIZE
          );
        } else {
          ctx.fillStyle = fillColor + '99';
          ctx.fillRect(
            x,
            iconY,
            PRIORITIZATION_ICON_SIZE,
            PRIORITIZATION_ICON_SIZE
          );
        }
      }

      ctx.restore();
    }
  }

  return new PrioritizationColumnHeaderIconExtension();
}
